/**
 * Route mapping utility for multilingual URLs
 * Uses Romanian slugs as primary, Russian uses transliterated Russian slugs with /ru/ prefix
 */

import { getLocalizedServiceSlug, getServiceIdFromSlug } from './serviceSlugMapping';
import { getLocalizedArticleSlug, getInternalArticleSlug } from './articleSlugMapping';

export type Language = 'ro' | 'ru';

// Romanian base routes (primary)
const romanianRoutes = [
  '/',
  '/servicii',
  '/portofoliu',
  '/articole',
  '/despre',
  '/contact',
  '/programare',
  '/politica-de-confidentialitate',
  '/termeni-si-conditii',
];

// Russian transliterated routes (with /ru/ prefix)
const russianRoutes = [
  '/ru',
  '/ru/uslugi',
  '/ru/portfolio',
  '/ru/stati',
  '/ru/o-nas',
  '/ru/kontakty',
  '/ru/zapis',
  '/ru/politika-konfidentsialnosti',
  '/ru/termeny-i-usloviya',
];

// Mapping between Romanian and Russian routes
const routeMapping: Record<string, string> = {
  // Romanian -> Russian
  '/': '/ru',
  '/servicii': '/ru/uslugi',
  '/portofoliu': '/ru/portfolio',
  '/articole': '/ru/stati',
  '/despre': '/ru/o-nas',
  '/contact': '/ru/kontakty',
  '/programare': '/ru/zapis',
  '/politica-de-confidentialitate': '/ru/politika-konfidentsialnosti',
  '/termeni-si-conditii': '/ru/termeny-i-usloviya',
  // Russian -> Romanian (reverse mapping)
  '/ru': '/',
  '/ru/uslugi': '/servicii',
  '/ru/portfolio': '/portofoliu',
  '/ru/stati': '/articole',
  '/ru/o-nas': '/despre',
  '/ru/kontakty': '/contact',
  '/ru/zapis': '/programare',
  '/ru/politika-konfidentsialnosti': '/politica-de-confidentialitate',
  '/ru/termeny-i-usloviya': '/termeni-si-conditii',
};

const baseRoutes = [...romanianRoutes, ...russianRoutes];

/**
 * Get the localized route for a given language
 */
export function getLocalizedRoute(path: string, language: Language): string {
  // Remove trailing slash for consistency
  const normalizedPath = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  
  // Handle dynamic routes (servicii/:id, articole/:slug for RO; uslugi/:id, stati/:slug for RU)
  if (normalizedPath.startsWith('/servicii/')) {
    const serviceSlugOrId = normalizedPath.replace('/servicii/', '');
    // Get internal service ID from Romanian slug (or use as-is if it's already an ID)
    const serviceId = getServiceIdFromSlug(serviceSlugOrId) || serviceSlugOrId;
    // Get localized slug for target language
    const localizedSlug = getLocalizedServiceSlug(serviceId as any, language);
    return language === 'ru' ? `/ru/uslugi/${localizedSlug}` : `/servicii/${localizedSlug}`;
  }
  
  if (normalizedPath.startsWith('/articole/')) {
    const articleSlugOrInternal = normalizedPath.replace('/articole/', '');
    // Get internal slug from Romanian slug (or use as-is if it's already internal)
    const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
    // Get localized slug for target language
    const localizedSlug = getLocalizedArticleSlug(internalSlug, language);
    return language === 'ru' ? `/ru/stati/${localizedSlug}` : `/articole/${localizedSlug}`;
  }
  
  // Handle /ru/uslugi/ and /ru/stati/ (already localized to Russian)
  if (normalizedPath.startsWith('/ru/uslugi/')) {
    const serviceSlugOrId = normalizedPath.replace('/ru/uslugi/', '');
    // Get internal service ID from Russian slug (or use as-is if it's already an ID)
    const serviceId = getServiceIdFromSlug(serviceSlugOrId) || serviceSlugOrId;
    // Get localized slug for target language
    const localizedSlug = getLocalizedServiceSlug(serviceId as any, language);
    return language === 'ru' ? `/ru/uslugi/${localizedSlug}` : `/servicii/${localizedSlug}`;
  }
  
  if (normalizedPath.startsWith('/ru/stati/')) {
    const articleSlugOrInternal = normalizedPath.replace('/ru/stati/', '');
    // Get internal slug from Russian slug (or use as-is if it's already internal)
    const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
    // Get localized slug for target language
    const localizedSlug = getLocalizedArticleSlug(internalSlug, language);
    return language === 'ru' ? `/ru/stati/${localizedSlug}` : `/articole/${localizedSlug}`;
  }
  
  // Get the base path (without language prefix if present)
  const basePath = getBaseRoute(normalizedPath);
  
  // For Russian, use the mapping to get Russian transliterated slug
  if (language === 'ru') {
    return routeMapping[basePath] || `/ru${basePath}`;
  }
  
  // For Romanian, return as-is
  return basePath;
}

/**
 * Get the base route (without language prefix, normalized to Romanian)
 */
export function getBaseRoute(path: string): string {
  // Handle dynamic routes first
  if (path.startsWith('/ru/uslugi/')) {
    const serviceSlugOrId = path.replace('/ru/uslugi/', '');
    // Get internal service ID from Russian slug
    const serviceId = getServiceIdFromSlug(serviceSlugOrId) || serviceSlugOrId;
    // Return Romanian path with Romanian slug
    const roSlug = getLocalizedServiceSlug(serviceId as any, 'ro');
    return `/servicii/${roSlug}`;
  }
  
  if (path.startsWith('/ru/stati/')) {
    const articleSlugOrInternal = path.replace('/ru/stati/', '');
    // Get internal slug from Russian slug
    const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
    // Return Romanian path with Romanian slug
    const roSlug = getLocalizedArticleSlug(internalSlug, 'ro');
    return `/articole/${roSlug}`;
  }
  
  if (path.startsWith('/articole/')) {
    // Normalize to use Romanian slug
    const articleSlugOrInternal = path.replace('/articole/', '');
    const internalSlug = getInternalArticleSlug(articleSlugOrInternal);
    const roSlug = getLocalizedArticleSlug(internalSlug, 'ro');
    return `/articole/${roSlug}`;
  }
  
  if (path.startsWith('/servicii/')) {
    // Normalize to use Romanian slug
    const serviceSlugOrId = path.replace('/servicii/', '');
    const serviceId = getServiceIdFromSlug(serviceSlugOrId) || serviceSlugOrId;
    const roSlug = getLocalizedServiceSlug(serviceId as any, 'ro');
    return `/servicii/${roSlug}`;
  }
  
  // For static Russian routes, use reverse mapping
  if (path.startsWith('/ru/') || path === '/ru') {
    // Try to find in mapping
    const mapped = routeMapping[path];
    if (mapped) {
      return mapped;
    }
    // Fallback: just remove /ru prefix
    return path === '/ru' ? '/' : path.replace('/ru/', '/');
  }
  
  return path;
}

/**
 * Detect language from URL path
 */
export function detectLanguageFromPath(path: string): Language {
  return path.startsWith('/ru/') || path === '/ru' ? 'ru' : 'ro';
}

/**
 * Get all routes for a given language (for sitemap generation)
 */
export function getAllRoutesForLanguage(language: Language): string[] {
  return baseRoutes.map(route => getLocalizedRoute(route, language));
}

/**
 * Get the alternate language URL for hreflang tags
 */
export function getAlternateLanguageUrl(currentPath: string, targetLanguage: Language): string {
  const baseRoute = getBaseRoute(currentPath);
  return getLocalizedRoute(baseRoute, targetLanguage);
}

/**
 * Check if a path is a valid route
 */
export function isValidRoute(path: string): boolean {
  const basePath = getBaseRoute(path);
  return romanianRoutes.includes(basePath) || 
         russianRoutes.includes(path) ||
         path.startsWith('/servicii/') || 
         path.startsWith('/articole/') || 
         path.startsWith('/ru/uslugi/') || 
         path.startsWith('/ru/stati/');
}


/** The internal service id of a service page path (`/servicii/<slug>`, `/ru/uslugi/<slug>`), or null. */
export function getServiceIdFromPath(path: string): string | null {
  const m = path.match(/^\/(?:ru\/uslugi|servicii)\/([^/?#]+)/);
  return m ? getServiceIdFromSlug(decodeURIComponent(m[1])) : null;
}
